/**
 * Discoverability Context
 *
 * Shared client API for tooltips, prompts, and onboarding tours.
 *
 * Primary responsibilities:
 * - Expose discoverability state and actions to UI components
 * - Keep optional and required hook access explicit
 *
 * Dependencies:
 * - React context
 */

'use client'

import { createContext, useContext } from 'react'

import type { DiscoverabilityEventType } from '@/db/discoverability'
import type { BetaFeatureKey } from '@/lib/beta-features/registry'
import type {
  DiscoverabilityFeatureKey,
  DiscoverabilityPlanGate,
  DiscoverabilitySurface,
  DiscoverabilityTrackedFeatureKey
} from './features'
import type {
  DiscoverabilityIntentSignals,
  DiscoverabilityPromptCandidate,
  DiscoverabilityUsageSnapshot
} from './getDiscoverabilityPrompts'
import type { DiscoverabilityState } from './storage'

export type DiscoverabilityTourId = 'main'

export type DiscoverabilityTooltipOptions = {
  expireDays?: number
  forceShow?: boolean
}

export type DiscoverabilityPromptRequest = {
  surface: DiscoverabilitySurface
  featureKeys?: DiscoverabilityFeatureKey[]
  route?: string | null
  trigger?: string | null
  intentSignals?: DiscoverabilityIntentSignals
  isTyping?: boolean
  isLoading?: boolean
  hasError?: boolean
  isSensitiveAction?: boolean
  /** Sidebar tips: skip the journey-card session budget. */
  ignoreSessionCap?: boolean
}

export type DiscoverabilityEventInput = {
  featureKey: DiscoverabilityTrackedFeatureKey
  eventType: DiscoverabilityEventType
  surface?: DiscoverabilitySurface | string | null
  trigger?: string | null
  metadata?: unknown
}

export type DiscoverabilityContextValue = {
  ready: boolean
  state: DiscoverabilityState
  usage: Partial<Record<DiscoverabilityTrackedFeatureKey, DiscoverabilityUsageSnapshot>>
  sessionPromptCount: number
  userPlan: DiscoverabilityPlanGate | string
  /** Live beta access — discoverability fail-closes on required betas until true. */
  enabledBetaFeatures: Partial<Record<BetaFeatureKey, boolean>>
  activeTourId: string | null
  isTourActive: boolean
  shouldShowTooltip: (featureId: string, options?: DiscoverabilityTooltipOptions) => boolean
  markTooltipSeen: (featureId: string) => void
  /** True while a local dismiss (checklist, card) is still in the snooze window. */
  isPromptDismissed: (promptId: string, snoozeDays?: number) => boolean
  /** Persist a local dismiss timestamp (defaults to 14-day snooze). */
  dismissPrompt: (promptId: string) => void
  getPrompt: (request: DiscoverabilityPromptRequest) => DiscoverabilityPromptCandidate | null
  markFeatureUsed: (input: {
    featureKey: DiscoverabilityTrackedFeatureKey
    surface?: DiscoverabilitySurface | string | null
    trigger?: string | null
    metadata?: unknown
  }) => Promise<void>
  recordEvent: (input: DiscoverabilityEventInput) => Promise<void>
  completeFeatureOnboarding: (featureKey: DiscoverabilityTrackedFeatureKey) => Promise<void>
  trigger: (triggerId: string) => void
  startTour: (tourId?: DiscoverabilityTourId) => void
  completeTour: (tourId?: DiscoverabilityTourId) => void
}

export const DiscoverabilityContext = createContext<DiscoverabilityContextValue | null>(null)

export const useOptionalDiscoverability = () => {
  return useContext(DiscoverabilityContext)
}

export const useDiscoverability = () => {
  const context = useOptionalDiscoverability()

  if (!context) {
    throw new Error('useDiscoverability must be used within DiscoverabilityProvider')
  }

  return context
}
